"use client";

import * as React from "react";

import { cn } from "../utils/utils";
import {
  RETRY_LABEL_EN,
  SUPPORT_REFERENCE_EN,
  translateError,
  type ErrorLocaleCatalog,
  type ErrorSeverity,
  type TranslatedError,
} from "../errors";
import { useErrorLocale } from "./ErrorLocaleProvider";
import { Alert, AlertDescription, AlertTitle } from "./ui/alert";
import { Button } from "./ui/button";

/**
 * The shadcn Alert primitive variants — kept in lockstep with
 * `packages/common/src/components/ui/alert.tsx` (cva variants `default`,
 * `destructive`, `warning`). Mirrors how `SystemHealthBanner` references the
 * same union.
 */
type AlertVariant = "default" | "destructive" | "warning";

export interface ErrorNoticePresentation {
  /** Which shadcn Alert variant this severity maps to. */
  variant: AlertVariant;
  /** ARIA role appropriate for the severity. */
  role: "alert" | "status";
  /** ARIA live priority appropriate for the severity. */
  ariaLive: "assertive" | "polite";
}

/**
 * Pure mapping from a translated severity to its presentation:
 *  - `service`     → amber/warning, polite (it's on us, not the user).
 *  - `onboarding`  → neutral/default with a "finish setup" tone, polite.
 *  - `user_input`  → destructive, assertive (the user must act).
 *  - `auth`        → destructive, assertive.
 *  - `unknown`     → destructive, assertive (never silently blend in).
 *
 * Exported separately so the mapping can be unit-tested without rendering
 * React — the @order-up/common package has no jsdom + @testing-library setup,
 * so tests run in node and assert on this pure function (mirrors
 * `getSystemHealthBannerVariant` in `SystemHealthBanner.tsx`). The component
 * below is a thin presentation wrapper that consumes this seam.
 */
export function errorNoticePresentation(
  severity: ErrorSeverity,
): ErrorNoticePresentation {
  switch (severity) {
    case "service":
      return { variant: "warning", role: "status", ariaLive: "polite" };
    case "onboarding":
      return { variant: "default", role: "status", ariaLive: "polite" };
    case "user_input":
    case "auth":
    case "unknown":
    default:
      return { variant: "destructive", role: "alert", ariaLive: "assertive" };
  }
}

export interface ErrorNoticeProps {
  /** Raw error; the component calls `translateError` internally. */
  error?: unknown;
  /** OR pass a pre-translated object (takes precedence over `error`). */
  translated?: TranslatedError;
  /** Forwarded to `translateError` context (popup-related copy). */
  providerName?: string;
  /**
   * The reader's language, from `errorLocaleCatalog(locale)`. Defaults to the
   * nearest `ErrorLocaleProvider`, else English.
   */
  localized?: ErrorLocaleCatalog;
  /** When set AND `translated.retryable`, render a "Try again" button. */
  onRetry?: () => void;
  /**
   * When true, the "Try again" button is disabled (and `aria-busy`) — used
   * while a retry is already in-flight so rapid clicks can't launch concurrent
   * recovery attempts. Undefined (default) leaves the button enabled.
   */
  retryBusy?: boolean;
  className?: string;
}

/**
 * Shared, presentational error surface. Translates a raw error (or accepts a
 * pre-translated one) and renders it on the shadcn `Alert` primitive with an
 * optional "Try again" affordance and any `nextSteps`.
 *
 * Returns `null` when there is nothing to show — either no error was supplied
 * or the translation is suppressed (e.g. the user closed a sign-in popup).
 */
export function ErrorNotice({
  error,
  translated,
  providerName,
  localized: localizedProp,
  onRetry,
  retryBusy,
  className,
}: ErrorNoticeProps): React.JSX.Element | null {
  const contextLocalized = useErrorLocale();
  const localized = localizedProp ?? contextLocalized;
  const resolved: TranslatedError | null =
    translated ??
    (error !== undefined && error !== null
      ? translateError(error, { providerName, localized })
      : null);

  if (!resolved || resolved.suppress) {
    return null;
  }

  const { variant, role, ariaLive } = errorNoticePresentation(
    resolved.severity,
  );
  const showRetry = Boolean(onRetry) && resolved.retryable;
  const hasNextSteps = Boolean(resolved.nextSteps && resolved.nextSteps.length);

  return (
    <Alert
      variant={variant}
      role={role}
      aria-live={ariaLive}
      // Read by the responsive sweep (e2e/responsive/lib/errorState.ts): a notice
      // on screen at load means the page's data failed, whatever its severity.
      data-error-notice={resolved.severity}
      className={cn(className)}
    >
      {resolved.title ? <AlertTitle>{resolved.title}</AlertTitle> : null}
      <AlertDescription>
        <span>{resolved.message}</span>
        {hasNextSteps ? (
          <ul className="list-disc pl-5 text-sm">
            {resolved.nextSteps!.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ul>
        ) : null}
        {showRetry ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="mt-2"
            onClick={onRetry}
            disabled={retryBusy}
            aria-busy={retryBusy}
          >
            {localized?.retryLabel ?? RETRY_LABEL_EN}
          </Button>
        ) : null}
        {resolved.technicalDetail ? (
          <p className="mt-2 text-xs break-words">
            {localized?.supportReference ?? SUPPORT_REFERENCE_EN}{" "}
            <span className="font-mono break-all">
              {resolved.technicalDetail}
            </span>
          </p>
        ) : null}
      </AlertDescription>
    </Alert>
  );
}
